import { useEffect, useState } from 'react';
import {
  Category,
  Post,
  RootQueryToPostConnection,
  Tag,
} from '../../../types/wp';
import { fetchWithType } from '../../../utils/api';
import { locationIsKia } from '../../../utils/helpers';
import { AppProps } from '../../App';
import StoryFilters from '../../components/story/StoryFilters';
import KIAImage from '../../components/ui/image/KIAImage';

export interface KiaStoryOptions {
  centerTitle: AppProps['centerTitle'];
  hideTitle: AppProps['hideTitle'];
}

const Story = ({
  heading,
  tag,
  category,
  centerTitle,
  hideTitle,
}: Pick<
  AppProps,
  'heading' | 'tag' | 'category' | 'centerTitle' | 'hideTitle'
>) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [testTag, setTestTag] = useState<string | null>(null);
  const [testCategory, setTestCategory] = useState<string | null>(null);
  const [testHeading, setTestHeading] = useState<string | null>(null);
  const [categoryOrTagName, setCategoryOrTagName] = useState<string | null>(
    null,
  );
  const [blockOptions, setBlockOptions] = useState<KiaStoryOptions>({
    centerTitle: centerTitle || false,
    hideTitle: hideTitle || false,
  });

  const postsFragment = `
    posts(first: 3) {
      nodes {
        link
        title
        excerpt
        categories {
          nodes {
            name
          }
        }
        featuredImage {
          node {
            sourceUrl(size: MEDIUM_LARGE)
            altText
          }
        }
      }
    }
  `;

  const postsQuery = `
    query getPosts {
      ${postsFragment}
    }
  `;

  const tagQuery = `
    query getPostsByTag {
      tag(id: "${testTag ?? tag}", idType: ID) {
        ${postsFragment}
        name
      }
    }
  `;

  const categoryQuery = `
    query getPostsByCategory {
      category(id: "${testCategory ?? category}", idType: ID) {
        ${postsFragment}
        name
      }
    }
  `;

  const options = {
    method: 'POST',
    body: JSON.stringify({
      query:
        category || testCategory
          ? categoryQuery
          : tag || testTag
            ? tagQuery
            : postsQuery,
      variables: {},
    }),
  };

  const fetchPosts = async () => {
    const { data, status } = await fetchWithType<
      | { data: { posts: RootQueryToPostConnection } }
      | { data: { tag: Tag } }
      | { data: { category: Category } }
    >('https://kia-blog.live.afonso.se/graphql', options);

    if (data?.data && !('errors' in data) && status === 200) {
      if ('posts' in data.data) {
        setPosts(data.data.posts.nodes);
      } else if ('tag' in data.data) {
        setCategoryOrTagName(data.data.tag.name);
        if (data.data.tag.posts?.nodes) {
          setPosts(data.data.tag.posts.nodes);
        }
      } else if ('category' in data.data) {
        setCategoryOrTagName(data.data.category.name);
        if (data.data.category.posts?.nodes) {
          setPosts(data.data.category.posts.nodes);
        }
      }
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (testTag) {
      fetchPosts();
    } else if (testCategory) {
      fetchPosts();
    }
  }, [testTag, testCategory]);

  return (
    <>
      {!locationIsKia() ? (
        <div className="my-10 max-w-screen-kia-sm mx-auto grid gap-2 px-4">
          <div className="">
            <h1 className="pb-4 text-3xl font-bold">
              Detta är ett block för att hämta blogginlägg från Kia Story
            </h1>
            <p className="grid">
              Justera följande filter och klicka sedan på "Visa kodsnutt" längst
              ned till höger
              <span className="font-bold">
                Om ingen tag eller kategori används så hämtas de tre senaste
                inläggen
              </span>
            </p>
          </div>
          <StoryFilters
            setTestCategory={setTestCategory}
            setTestTag={setTestTag}
            setTestHeading={setTestHeading}
            testTag={testTag}
            testCategory={testCategory}
            testHeading={testHeading}
            blockOptions={blockOptions}
            setBlockOptions={setBlockOptions}
          />
        </div>
      ) : null}
      <section className="my-10 max-w-screen-kia-sm mx-auto">
        {blockOptions.hideTitle ? null : (
          <h2
            data-center={blockOptions.centerTitle || false}
            className="mb-8 text-2xl font-bold px-4 md:px-1 data-[center='true']:text-center"
          >
            {testHeading ?? heading ?? 'Kia Story'}
          </h2>
        )}
        <ul className="grid grid-flow-col auto-cols-[80vw] md:auto-cols-fr snap-x snap-mandatory overflow-x-auto md:px-1 md:gap-4 pb-4 md:pb-8 h-full">
          {posts.map((post, i) => (
            <li
              key={i}
              className={`${i === 0 ? 'snap-start' : i === 1 ? 'snap-center' : 'snap-end'} no-underline relative group px-2 md:px-0 `}
            >
              <div className="post w-full shadow-md shadow-black/6 h-full rounded overflow-hidden grid grid-rows-[auto_1fr]">
                <div className="px-5 md:px-6 pt-5 pb-6 md:pb-8 h-full">
                  <h4 className="flex items-center group-hover:underline text-2xl font-bold mb-3">
                    <span>{post.title}</span>
                  </h4>
                  <div
                    className="text-base font-light line-clamp-6"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  ></div>
                  <a
                    href={post.link}
                    target="_blank"
                    className="absolute inset-0"
                  >
                    <span className="sr-only">{post.title}</span>
                  </a>
                </div>
                <div className="overflow-hidden aspect-[4/3] -order-1">
                  <KIAImage
                    loading="lazy"
                    width="300"
                    height="300"
                    className="w-full h-full object-cover group-hover:scale-[1.025] transition-all duration-500"
                    src={post.featuredImage?.node?.sourceUrl}
                    alt={post.featuredImage?.node?.altText ?? post.title}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
};

export default Story;
